import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  MenuItem,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
import { addDays, getDay, format, startOfDay, subYears } from "date-fns";
import { usePermissions } from "../../hooks/usePermissions";
import {
  TIMETABLE_CONVERT,
  TIMETABLE_CONVERT_OWN,
} from "../../utils/timetablePermissions";
import { DATE_DISPLAY_FORMAT } from "../../utils/dateUtils";
import conversionService from "../../services/conversionService";
import api from "../../services/api";
import courseService from "../../services/courseService";

/**
 * Map dayOfWeek string/number from slot to JS Date getDay() value (0=Sun, 1=Mon, ...).
 * The backend stores dayOfWeek as a string like "Monday", "Tuesday", etc.
 * Mirrors EditLectureForm.js conventions.
 */
const DAY_NAME_TO_NUMBER = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const MIN_SLOT_MINUTES = 30;

function getDayNumber(dayOfWeek) {
  if (dayOfWeek == null) return -1;
  if (typeof dayOfWeek === "number") return dayOfWeek;
  const normalized = String(dayOfWeek).toLowerCase().trim();
  return DAY_NAME_TO_NUMBER[normalized] ?? -1;
}

/** Parse an "HH:MM" (24h) string into minutes-since-midnight. Returns NaN when malformed. */
function timeToMinutes(hhmm) {
  if (typeof hhmm !== "string") return NaN;
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return NaN;
  const hours = Number(m[1]);
  const mins = Number(m[2]);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(mins) ||
    hours < 0 ||
    hours > 23 ||
    mins < 0 ||
    mins > 59
  ) {
    return NaN;
  }
  return hours * 60 + mins;
}

/** Convert minutes-since-midnight into a zero-padded "HH:MM" 24h string. */
function minutesToTime(total) {
  if (!Number.isFinite(total)) return "";
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(total)));
  const hh = Math.floor(clamped / 60);
  const mm = clamped % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/**
 * Normalize a raw slot time value (may be "HH:MM", "HH:MM:SS", Date-ish) to "HH:MM".
 * Falls back to empty string when it can't be parsed.
 */
function normalizeTime(value) {
  if (value == null) return "";
  const str = String(value).trim();
  const m = str.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;
}

/**
 * Build N consecutive target slots that fully cover [startMin, endMin] with
 * equal / near-equal splits. Remainder minutes are given to the EARLIER slots.
 * Guarantees: consecutive (each end === next start), covers full span
 * (first.start === startMin, last.end === endMin).
 */
function buildEqualSplits(startMin, endMin, count) {
  const total = endMin - startMin;
  const n = Math.max(1, count);
  const base = Math.floor(total / n);
  const remainder = total - base * n;
  const slots = [];
  let cursor = startMin;
  for (let i = 0; i < n; i++) {
    // give one extra minute to the first `remainder` slots
    const size = base + (i < remainder ? 1 : 0);
    const slotStart = cursor;
    // the last slot always snaps to endMin to guarantee full coverage
    const slotEnd = i === n - 1 ? endMin : cursor + size;
    slots.push({ startMin: slotStart, endMin: slotEnd });
    cursor = slotEnd;
  }
  return slots;
}

/** Helper to avoid nested ternary in JSX for the date field. */
function getDateHelperText(isDateValid, overrideDate, dayOfWeek) {
  if (!isDateValid && overrideDate) {
    return "Date must match the slot's weekday (past dates allowed, up to 7 days ahead)";
  }
  return `Only ${dayOfWeek || "matching"} dates allowed`;
}

/** Retry-aware helper text for the async dropdowns (teachers/subjects). */
function getListHelperText(errorText, onRetry) {
  if (!errorText) return null;
  return (
    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {errorText}
      <Chip label="Retry" size="small" onClick={onRetry} sx={{ ml: 1, cursor: "pointer" }} />
    </Box>
  );
}

/**
 * ConvertLabToTheoryModal — Modal to convert a lab block (N parallel batch slots)
 * into M consecutive theory lectures for a specific date (LAB_TO_THEORY).
 *
 * Mirrors EditLectureForm.js conventions (MUI Dialog/Autocomplete/DatePicker,
 * date-fns, react-toastify, usePermissions, teacher list via
 * `/teacher/get-teachers-names`, subjects via courseService.getAllSubjects).
 *
 * Props:
 * - open: boolean — controls dialog visibility
 * - onClose: () => void — called when the dialog should close
 * - sourceSlots: array — lab batch slot objects; each has
 *     { id, teacher: {id,name}, subject: {id,name}, practicalBatchCode,
 *       startTime, endTime, dayOfWeek }
 *     All batches share the same startTime/endTime (parallel batches).
 * - classId: number — the class the conversion targets
 * - className: string — display label for the class
 * - date: Date | string — the date the parent surfaced (used as the default)
 * - onSuccess: () => void — called after a successful conversion (parent refresh)
 */
const ConvertLabToTheoryModal = ({
  open,
  onClose,
  sourceSlots,
  classId,
  className,
  date,
  onSuccess,
}) => {
  const { hasPermission } = usePermissions();
  // Visibility is enforced by the parent; kept here for parity / defensive gating.
  const canConvert =
    hasPermission(TIMETABLE_CONVERT) || hasPermission(TIMETABLE_CONVERT_OWN);

  // Defensive normalization — never trust the incoming array shape (no-crash-guarantee).
  const safeSourceSlots = useMemo(
    () => (Array.isArray(sourceSlots) ? sourceSlots.filter(Boolean) : []),
    [sourceSlots]
  );

  const firstSlot = safeSourceSlots[0] || null;

  // Lab block bounds — all batches share start/end, so use the first slot.
  const labStart = useMemo(() => normalizeTime(firstSlot?.startTime), [firstSlot]);
  const labEnd = useMemo(() => normalizeTime(firstSlot?.endTime), [firstSlot]);
  const labStartMin = useMemo(() => timeToMinutes(labStart), [labStart]);
  const labEndMin = useMemo(() => timeToMinutes(labEnd), [labEnd]);

  const durationMinutes = useMemo(() => {
    if (Number.isNaN(labStartMin) || Number.isNaN(labEndMin)) return 0;
    const d = labEndMin - labStartMin;
    return d > 0 ? d : 0;
  }, [labStartMin, labEndMin]);

  // Max number of theory lectures — each must be >= 30 min.
  const maxCount = useMemo(() => {
    if (durationMinutes <= 0) return 1;
    return Math.max(1, Math.floor(durationMinutes / MIN_SLOT_MINUTES));
  }, [durationMinutes]);

  // Default count: ~1 per hour, capped at maxCount, at least 1.
  const defaultCount = useMemo(() => {
    if (durationMinutes <= 0) return 1;
    const perHour = Math.max(1, Math.round(durationMinutes / 60));
    return Math.min(maxCount, perHour);
  }, [durationMinutes, maxCount]);

  const slotDayNumber = useMemo(
    () => getDayNumber(firstSlot?.dayOfWeek),
    [firstSlot]
  );

  const today = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => addDays(today, 7), [today]);
  // Past dates allowed (gated server-side by marked attendance); far-past floor.
  const minDate = useMemo(() => subYears(today, 1), [today]);

  // --- State ---
  const [count, setCount] = useState(1);
  // targets: array of { endTime: "HH:MM", teacher, subject }.
  // Each target's START is derived from the previous target's endTime (consecutive
  // by construction). The last target's endTime is pinned to the lab end.
  const [targets, setTargets] = useState([]);
  const [overrideDate, setOverrideDate] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Teacher dropdown state
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teachersError, setTeachersError] = useState(null);

  // Subject dropdown state
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState(null);

  // --- Fetch teacher list (mirrors EditLectureForm) ---
  const fetchTeachers = useCallback(async () => {
    setTeachersLoading(true);
    setTeachersError(null);
    try {
      const response = await api.get("/teacher/get-teachers-names");
      const data = response?.data?.data || response?.data || [];
      const teacherList = Array.isArray(data)
        ? data.map((t) => ({
            id: t?.id || t?.teacherId,
            name:
              t?.name ||
              t?.fullName ||
              `${t?.firstName || ""} ${t?.lastName || ""}`.trim(),
          }))
        : [];
      setTeachers(teacherList);
    } catch (err) {
      console.error("[ConvertLabToTheoryModal:fetchTeachers] Error:", err?.message || err);
      setTeachersError(err?.response?.data?.message || "Failed to load teachers");
    } finally {
      setTeachersLoading(false);
    }
  }, []);

  // --- Fetch subject list (mirrors EditLectureForm) ---
  const fetchSubjects = useCallback(async () => {
    setSubjectsLoading(true);
    setSubjectsError(null);
    try {
      const response = await courseService.getAllSubjects({ limit: 500 });
      const data =
        response?.data?.data?.subjects ||
        response?.data?.data ||
        response?.data ||
        [];
      const subjectList = Array.isArray(data)
        ? data.map((s) => ({
            id: s?.id,
            name: s?.name || s?.subjectName || "",
          }))
        : [];
      setSubjects(subjectList);
    } catch (err) {
      console.error("[ConvertLabToTheoryModal:fetchSubjects] Error:", err?.message || err);
      setSubjectsError(err?.response?.data?.message || "Failed to load subjects");
    } finally {
      setSubjectsLoading(false);
    }
  }, []);

  // Fetch dropdown data when the dialog opens.
  useEffect(() => {
    if (open) {
      fetchTeachers();
      fetchSubjects();
    }
  }, [open, fetchTeachers, fetchSubjects]);

  // Initialize count when dialog opens / lab bounds change.
  useEffect(() => {
    if (!open) return;
    setCount(defaultCount);
  }, [open, defaultCount]);

  // (Re)generate equal-split targets whenever count / lab bounds change.
  // Preserves already-picked teacher/subject where an index still exists.
  useEffect(() => {
    if (!open) return;
    if (durationMinutes <= 0) {
      setTargets([]);
      return;
    }
    const splits = buildEqualSplits(labStartMin, labEndMin, count);
    setTargets((prev) =>
      splits.map((s, i) => ({
        endTime: minutesToTime(s.endMin),
        teacher: prev[i]?.teacher ?? null,
        subject: prev[i]?.subject ?? null,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, count, labStartMin, labEndMin, durationMinutes]);

  // Default the override date to the passed `date` prop when valid, else the
  // next matching weekday within 7 days.
  useEffect(() => {
    if (!open || slotDayNumber < 0) return;
    // Try the passed date prop first.
    let seed = null;
    if (date) {
      const parsed = date instanceof Date ? date : new Date(date);
      if (!Number.isNaN(parsed?.getTime?.())) {
        const d0 = startOfDay(parsed);
        if (d0 >= minDate && d0 <= maxDate && getDay(d0) === slotDayNumber) {
          seed = d0;
        }
      }
    }
    if (!seed) {
      const todayDay = getDay(today);
      if (todayDay === slotDayNumber) {
        seed = today;
      } else {
        for (let i = 1; i <= 7; i++) {
          const candidate = addDays(today, i);
          if (getDay(candidate) === slotDayNumber) {
            seed = candidate;
            break;
          }
        }
      }
    }
    setOverrideDate(seed);
  }, [open, slotDayNumber, today, minDate, maxDate, date]);

  // Reset the form on close.
  useEffect(() => {
    if (!open) {
      setCount(1);
      setTargets([]);
      setOverrideDate(null);
      setReason("");
      setSubmitting(false);
    }
  }, [open]);

  // --- Derived: resolved target time ranges (start derived from previous end) ---
  const resolvedTargets = useMemo(() => {
    if (durationMinutes <= 0 || targets.length === 0) return [];
    const out = [];
    let startMin = labStartMin;
    for (let i = 0; i < targets.length; i++) {
      const t = targets[i];
      // Last slot always ends at the lab end (guarantees full coverage).
      const endMin =
        i === targets.length - 1 ? labEndMin : timeToMinutes(t.endTime);
      out.push({
        startMin,
        endMin,
        startTime: minutesToTime(startMin),
        endTime: i === targets.length - 1 ? labEnd : t.endTime,
        teacher: t.teacher,
        subject: t.subject,
      });
      startMin = endMin;
    }
    return out;
  }, [targets, durationMinutes, labStartMin, labEndMin, labEnd]);

  // --- Time validation: consecutive, covers full span, each >= 30 min ---
  const timeValidation = useMemo(() => {
    if (durationMinutes <= 0) {
      return { valid: false, error: "Lab block has no valid duration" };
    }
    if (resolvedTargets.length === 0) {
      return { valid: false, error: "No target lectures configured" };
    }
    for (let i = 0; i < resolvedTargets.length; i++) {
      const { startMin, endMin } = resolvedTargets[i];
      if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) {
        return { valid: false, error: `Lecture ${i + 1} has an invalid time` };
      }
      if (endMin <= startMin) {
        return {
          valid: false,
          error: `Lecture ${i + 1} end time must be after its start time`,
        };
      }
      if (endMin - startMin < MIN_SLOT_MINUTES) {
        return {
          valid: false,
          error: `Lecture ${i + 1} must be at least ${MIN_SLOT_MINUTES} minutes`,
        };
      }
    }
    // Full coverage: first start === lab start, last end === lab end.
    if (resolvedTargets[0].startMin !== labStartMin) {
      return { valid: false, error: "First lecture must start at the lab start time" };
    }
    if (resolvedTargets[resolvedTargets.length - 1].endMin !== labEndMin) {
      return { valid: false, error: "Last lecture must end at the lab end time" };
    }
    return { valid: true, error: null };
  }, [resolvedTargets, durationMinutes, labStartMin, labEndMin]);

  const allTargetsAssigned = useMemo(
    () =>
      resolvedTargets.length > 0 &&
      resolvedTargets.every(
        (t) => t.teacher?.id != null && t.subject?.id != null
      ),
    [resolvedTargets]
  );

  const isDateValid = useMemo(() => {
    if (!overrideDate) return false;
    const d = startOfDay(overrideDate);
    // Past dates allowed; only enforce the far-past floor and the +7 forward cap.
    if (d < minDate || d > maxDate) return false;
    if (getDay(d) !== slotDayNumber) return false;
    return true;
  }, [overrideDate, minDate, maxDate, slotDayNumber]);

  const canSubmit =
    canConvert &&
    safeSourceSlots.length > 0 &&
    allTargetsAssigned &&
    timeValidation.valid &&
    isDateValid &&
    !submitting &&
    !teachersLoading &&
    !subjectsLoading;

  // --- Date restriction (mirrors EditLectureForm) ---
  const shouldDisableDate = useCallback(
    (d) => {
      if (!d) return true;
      const dt = d instanceof Date ? d : new Date(d);
      return getDay(dt) !== slotDayNumber;
    },
    [slotDayNumber]
  );

  // --- Target field handlers ---
  const handleTargetEndTimeChange = (index, value) => {
    setTargets((prev) =>
      prev.map((t, i) => (i === index ? { ...t, endTime: value } : t))
    );
  };
  const handleTargetTeacherChange = (index, value) => {
    setTargets((prev) =>
      prev.map((t, i) => (i === index ? { ...t, teacher: value } : t))
    );
  };
  const handleTargetSubjectChange = (index, value) => {
    setTargets((prev) =>
      prev.map((t, i) => (i === index ? { ...t, subject: value } : t))
    );
  };

  // --- Submit ---
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        conversionType: "LAB_TO_THEORY",
        classId,
        overrideDate: format(overrideDate, "yyyy-MM-dd"),
        sourceSlotIds: safeSourceSlots
          .map((s) => s?.id)
          .filter((id) => id != null),
        targetSlots: resolvedTargets.map((t) => ({
          startTime: t.startTime,
          endTime: t.endTime,
          teacherId: t.teacher.id,
          subjectId: t.subject.id,
          lectureType: "THEORY",
          practicalBatchCode: null,
        })),
      };
      const trimmedReason = reason.trim();
      if (trimmedReason) payload.reason = trimmedReason;

      await conversionService.createConversion(payload);
      toast.success("Lab converted to theory successfully");
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("[ConvertLabToTheoryModal:handleSubmit] Error:", err?.message || err);
      const message =
        err?.response?.data?.message || "Failed to convert lab to theory";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Count selector options (1..maxCount).
  const countOptions = useMemo(
    () => Array.from({ length: maxCount }, (_, i) => i + 1),
    [maxCount]
  );

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>Convert Lab to Theory</DialogTitle>
      <DialogContent dividers>
        {safeSourceSlots.length === 0 ? (
          <Box sx={{ py: 3 }}>
            <Typography variant="body2" color="error">
              No lab slots were provided to convert.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Read-only source lab block */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Lab Block
              </Typography>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Class:</strong> {className || firstSlot?.className || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Day:</strong> {firstSlot?.dayOfWeek || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Time:</strong> {labStart || "—"} – {labEnd || "—"}
                    {durationMinutes > 0 ? ` (${durationMinutes} min)` : ""}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {safeSourceSlots.map((s, i) => (
                  <Typography
                    key={s?.id ?? `src-${i}`}
                    variant="body2"
                    color="text.secondary"
                  >
                    <Chip
                      label={s?.practicalBatchCode || `Batch ${i + 1}`}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {s?.teacher?.name || "—"} · {s?.subject?.name || "—"}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Number of theory lectures */}
            <Box sx={{ mb: 2 }}>
              <TextField
                select
                label="Number of theory lectures"
                value={countOptions.includes(count) ? count : ""}
                onChange={(e) => setCount(Number(e.target.value))}
                sx={{ width: 260 }}
                helperText={`1 to ${maxCount} (each lecture ≥ ${MIN_SLOT_MINUTES} min)`}
                disabled={durationMinutes <= 0}
              >
                {countOptions.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Target theory sections */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Theory Lectures
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              {resolvedTargets.map((t, index) => {
                const isLast = index === resolvedTargets.length - 1;
                return (
                  <Paper
                    key={`target-${index}`}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2 }}
                  >
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                      Lecture {index + 1}
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="Start"
                          type="time"
                          value={t.startTime}
                          disabled
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          helperText="Auto"
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          label="End"
                          type="time"
                          value={t.endTime}
                          onChange={(e) =>
                            handleTargetEndTimeChange(index, e.target.value)
                          }
                          disabled={isLast}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            step: 300,
                            min: labStart || undefined,
                            max: labEnd || undefined,
                          }}
                          helperText={isLast ? "Lab end" : "Editable"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={teachers}
                          getOptionLabel={(o) => o?.name || ""}
                          value={t.teacher}
                          onChange={(_, value) =>
                            handleTargetTeacherChange(index, value)
                          }
                          isOptionEqualToValue={(o, v) => o?.id === v?.id}
                          loading={teachersLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Teacher"
                              placeholder="Search teachers..."
                              error={Boolean(teachersError)}
                              helperText={getListHelperText(
                                teachersError,
                                fetchTeachers
                              )}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {teachersLoading ? (
                                      <CircularProgress size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={subjects}
                          getOptionLabel={(o) => o?.name || ""}
                          value={t.subject}
                          onChange={(_, value) =>
                            handleTargetSubjectChange(index, value)
                          }
                          isOptionEqualToValue={(o, v) => o?.id === v?.id}
                          loading={subjectsLoading}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Subject"
                              placeholder="Search subjects..."
                              error={Boolean(subjectsError)}
                              helperText={getListHelperText(
                                subjectsError,
                                fetchSubjects
                              )}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {subjectsLoading ? (
                                      <CircularProgress size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Box>

            {/* Time validation feedback */}
            {!timeValidation.valid && timeValidation.error ? (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                {timeValidation.error}
              </Typography>
            ) : null}

            <Divider sx={{ my: 2 }} />

            {/* Date + reason */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <DatePicker
                label="Conversion Date"
                format={DATE_DISPLAY_FORMAT}
                value={overrideDate}
                onChange={(d) => setOverrideDate(d)}
                minDate={minDate}
                maxDate={maxDate}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: getDateHelperText(
                      isDateValid,
                      overrideDate,
                      firstSlot?.dayOfWeek
                    ),
                  },
                }}
              />
              <TextField
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                fullWidth
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                helperText={`${reason.length}/500`}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={
            submitting ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {submitting ? "Converting..." : "Convert to Theory"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConvertLabToTheoryModal;
